import { supabase } from '../lib/supabase'
import { fetchAllByRange } from '../lib/supabaseFetchAll'
import {
  brokerLabelFromEmbed,
  comercialContatosPortalService,
  formatDateYmd,
} from './comercialContatosPortal'
import type {
  RelatorioDashboardMetricas,
  RelatorioDashboardParams,
  RelatorioDashboardPorDia,
  RelatorioDashboardPorMeio,
  RelatorioDashboardPorTipo,
  RelatorioDashboardResult,
  RelatorioDashboardTopCorretora,
  RelatorioDashboardTopMotivo,
} from '../types/comercialContatosPortal'

export type {
  RelatorioDashboardMetricas,
  RelatorioDashboardParams,
  RelatorioDashboardPorDia,
  RelatorioDashboardPorMeio,
  RelatorioDashboardPorTipo,
  RelatorioDashboardResult,
  RelatorioDashboardTopCorretora,
  RelatorioDashboardTopMotivo,
} from '../types/comercialContatosPortal'

const RPC_NAME = 'get_relatorio_contatos_dashboard'

const contactDashboardSelect =
  'broker_id,contact_date,call_direction,contact_channel,commercial_contact_reason_id,reason,registered_by_user_id,commercial_contact_reason:helpdesk_commercial_contact_reasons(name),broker:brokers!helpdesk_broker_contact_logs_broker_id_fkey(name)'

const diaryDashboardSelect = 'id,broker_id,created_at,created_by'

function getDefaultDashboardDateRange(): { data_inicio: string; data_fim: string } {
  const fim = new Date()
  const inicio = new Date()
  inicio.setDate(inicio.getDate() - 30)
  return { data_inicio: formatDateYmd(inicio), data_fim: formatDateYmd(fim) }
}

function isRpcUnavailableError(message: string): boolean {
  const m = message.toLowerCase()
  return (
    m.includes('could not find the function') ||
    (m.includes('function') && m.includes('does not exist')) ||
    m.includes('42883') ||
    m.includes('pgrst202')
  )
}

function motivoLabelFromRow(row: {
  reason?: string | null
  commercial_contact_reason?: { name?: string | null } | { name?: string | null }[] | null
}): string {
  const cr = Array.isArray(row.commercial_contact_reason)
    ? row.commercial_contact_reason[0]
    : row.commercial_contact_reason
  const nome = typeof cr?.name === 'string' ? cr.name.trim() : ''
  if (nome) return nome
  const legado = typeof row.reason === 'string' ? row.reason.trim() : ''
  return legado || 'Sem motivo'
}

function meioLabel(meio: string): string {
  if (meio === 'telefone') return 'Telefone'
  if (meio === 'email') return 'E-Mail'
  if (meio === 'whatsapp') return 'WhatsApp'
  return meio || 'Outro'
}

function tipoLabel(tipo: string): string {
  if (tipo === 'ativo') return 'Ativo'
  if (tipo === 'receptivo') return 'Receptivo'
  return tipo || 'Outro'
}

function parseRpcPayload(raw: unknown): RelatorioDashboardMetricas | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  return {
    total_contatos: Number(o.total_contatos ?? 0),
    total_diario: Number(o.total_diario ?? 0),
    corretoras_distintas: Number(o.corretoras_distintas ?? 0),
    pct_ativo: Number(o.pct_ativo ?? 0),
    por_dia: Array.isArray(o.por_dia) ? (o.por_dia as RelatorioDashboardPorDia[]) : [],
    por_meio: Array.isArray(o.por_meio) ? (o.por_meio as RelatorioDashboardPorMeio[]) : [],
    por_tipo: Array.isArray(o.por_tipo) ? (o.por_tipo as RelatorioDashboardPorTipo[]) : [],
    top_corretoras: Array.isArray(o.top_corretoras)
      ? (o.top_corretoras as RelatorioDashboardTopCorretora[])
      : [],
    top_motivos: Array.isArray(o.top_motivos)
      ? (o.top_motivos as RelatorioDashboardTopMotivo[])
      : [],
    data_inicio: String(o.data_inicio ?? ''),
    data_fim: String(o.data_fim ?? ''),
  }
}

async function fetchDashboardViaRpc(
  data_inicio: string,
  data_fim: string,
  broker_id?: string | null
): Promise<RelatorioDashboardResult> {
  const { data, error } = await supabase.rpc(RPC_NAME, {
    p_data_inicio: data_inicio,
    p_data_fim: data_fim,
    p_responsavel_id: null,
    p_broker_id: broker_id ?? null,
  })

  if (error) {
    return { data: null, error: error.message, source: 'rpc' }
  }

  if (data == null) {
    return { data: null, error: 'Sem permissão ou dados indisponíveis.', source: 'rpc' }
  }

  const parsed = parseRpcPayload(data)
  if (!parsed) {
    return { data: null, error: 'Resposta inválida do servidor.', source: 'rpc' }
  }

  return { data: parsed, source: 'rpc' }
}

type ContactDashboardRow = {
  broker_id: string
  contact_date: string
  call_direction: string
  contact_channel: string | null
  commercial_contact_reason_id: string | null
  reason: string | null
  registered_by_user_id: string | null
  commercial_contact_reason?: { name?: string | null } | { name?: string | null }[] | null
  broker?:
    | { holder_name?: string | null; name?: string | null }
    | { holder_name?: string | null; name?: string | null }[]
    | null
}

type DiaryDashboardRow = {
  id: string
  broker_id: string
  created_at: string
  created_by: string
}

async function fetchDashboardViaFallback(
  data_inicio: string,
  data_fim: string,
  broker_ids?: string[] | null
): Promise<RelatorioDashboardResult> {
  const [contatosRes, diarioRes] = await Promise.all([
    fetchAllByRange((from, to) => {
      let q = supabase
        .from('helpdesk_broker_contact_logs')
        .select(contactDashboardSelect)
        .gte('contact_date', data_inicio)
        .lte('contact_date', data_fim)
        .order('id', { ascending: true })
        .range(from, to)
      if (broker_ids?.length === 1) q = q.eq('broker_id', broker_ids[0])
      else if (broker_ids && broker_ids.length > 1) q = q.in('broker_id', broker_ids)
      return q
    }),
    fetchAllByRange((from, to) => {
      let q = supabase
        .from('helpdesk_broker_diary')
        .select(diaryDashboardSelect)
        .gte('created_at', `${data_inicio}T00:00:00`)
        .lte('created_at', `${data_fim}T23:59:59.999`)
        .order('id', { ascending: true })
        .range(from, to)
      if (broker_ids?.length === 1) q = q.eq('broker_id', broker_ids[0])
      else if (broker_ids && broker_ids.length > 1) q = q.in('broker_id', broker_ids)
      return q
    }),
  ])

  if (contatosRes.error) {
    return { data: null, error: contatosRes.error, source: 'fallback' }
  }
  if (diarioRes.error) {
    return { data: null, error: diarioRes.error, source: 'fallback' }
  }

  const contatos = (contatosRes.data ?? []) as ContactDashboardRow[]
  const diario = (diarioRes.data ?? []) as DiaryDashboardRow[]

  const porDiaMap = new Map<string, number>()
  const porMeioMap = new Map<string, number>()
  const porTipoMap = new Map<string, number>()
  const corretorasMap = new Map<string, { nome: string; quantidade: number }>()
  const motivosMap = new Map<string, { nome: string; quantidade: number }>()
  const brokersDistintos = new Set<string>()
  let ativos = 0

  for (const row of contatos) {
    const dia = String(row.contact_date).slice(0, 10)
    porDiaMap.set(dia, (porDiaMap.get(dia) ?? 0) + 1)

    if (row.contact_channel) {
      const meio = String(row.contact_channel)
      porMeioMap.set(meio, (porMeioMap.get(meio) ?? 0) + 1)
    }

    if (row.call_direction) {
      const tipo = String(row.call_direction)
      porTipoMap.set(tipo, (porTipoMap.get(tipo) ?? 0) + 1)
      if (tipo === 'ativo') ativos++
    }

    if (row.broker_id) {
      brokersDistintos.add(row.broker_id)
      const nome = brokerLabelFromEmbed(row.broker)
      const prev = corretorasMap.get(row.broker_id)
      corretorasMap.set(row.broker_id, {
        nome,
        quantidade: (prev?.quantidade ?? 0) + 1,
      })
    }

    const motivoId = row.commercial_contact_reason_id?.trim() || ''
    const motivoNome = motivoLabelFromRow(row)
    const motivoKey = motivoId || motivoNome
    const prevMotivo = motivosMap.get(motivoKey)
    motivosMap.set(motivoKey, {
      nome: motivoNome,
      quantidade: (prevMotivo?.quantidade ?? 0) + 1,
    })
  }

  const totalContatos = contatos.length
  const pctAtivo = totalContatos === 0 ? 0 : Math.round((1000 * ativos) / totalContatos) / 10

  const por_dia = Array.from(porDiaMap.entries())
    .map(([dia, quantidade]) => ({ dia, quantidade }))
    .sort((a, b) => a.dia.localeCompare(b.dia))

  const por_meio = Array.from(porMeioMap.entries())
    .map(([meio, quantidade]) => ({ meio, label: meioLabel(meio), quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)

  const por_tipo = Array.from(porTipoMap.entries())
    .map(([tipo, quantidade]) => ({ tipo, label: tipoLabel(tipo), quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)

  const top_corretoras = Array.from(corretorasMap.entries())
    .map(([broker_id, v]) => ({ broker_id, nome: v.nome, quantidade: v.quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)
    .slice(0, 10)

  const top_motivos = Array.from(motivosMap.entries())
    .map(([motivo_id, v]) => ({ motivo_id, nome: v.nome, quantidade: v.quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade)
    .slice(0, 10)

  return {
    data: {
      total_contatos: totalContatos,
      total_diario: diario.length,
      corretoras_distintas: brokersDistintos.size,
      pct_ativo: pctAtivo,
      por_dia,
      por_meio,
      por_tipo,
      top_corretoras,
      top_motivos,
      data_inicio,
      data_fim,
    },
    source: 'fallback',
  }
}

async function fetchRelatorioContatosDashboard(
  params: RelatorioDashboardParams = {}
): Promise<RelatorioDashboardResult> {
  const defaults = getDefaultDashboardDateRange()
  const data_inicio = params.data_inicio ?? defaults.data_inicio
  const data_fim = params.data_fim ?? defaults.data_fim

  const resolved = await comercialContatosPortalService.resolveBrokerIdsForComercialFiltro({
    broker_id: params.broker_id ?? undefined,
    broker_ids: params.broker_ids ?? undefined,
    comercial_user_id: params.comercial_user_id ?? undefined,
    consultant_user_id: params.consultant_user_id ?? undefined,
  })

  if (resolved.empty) {
    return {
      data: {
        total_contatos: 0,
        total_diario: 0,
        corretoras_distintas: 0,
        pct_ativo: 0,
        por_dia: [],
        por_meio: [],
        por_tipo: [],
        top_corretoras: [],
        top_motivos: [],
        data_inicio,
        data_fim,
      },
      source: 'fallback',
    }
  }

  const brokerIds = resolved.broker_ids

  // RPC só aceita um broker_id; com vários (filtro de comercial) usa fallback.
  // Também faz fallback se a RPC existir mas retornar null (ex.: permissão desatualizada no SQL).
  if (!brokerIds || brokerIds.length <= 1) {
    const rpcResult = await fetchDashboardViaRpc(data_inicio, data_fim, brokerIds?.[0] ?? null)
    if (rpcResult.data) return rpcResult

    if (rpcResult.error && !isRpcUnavailableError(rpcResult.error)) {
      console.warn(
        '[comercialContatosPortalDashboard] RPC indisponível/sem dados; usando fallback:',
        rpcResult.error
      )
    }
  }

  return fetchDashboardViaFallback(data_inicio, data_fim, brokerIds)
}

function fillPorDiaSeries(
  por_dia: RelatorioDashboardPorDia[],
  data_inicio: string,
  data_fim: string
): { label: string; quantidade: number }[] {
  const map = new Map(por_dia.map(p => [p.dia, p.quantidade]))
  const result: { label: string; quantidade: number }[] = []
  const inicio = new Date(`${data_inicio}T12:00:00`)
  const fim = new Date(`${data_fim}T12:00:00`)
  const mesesNomes = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ]

  for (let d = new Date(inicio); d <= fim; d.setDate(d.getDate() + 1)) {
    const chave = formatDateYmd(d)
    result.push({
      label: `${String(d.getDate()).padStart(2, '0')}/${mesesNomes[d.getMonth()]}`,
      quantidade: map.get(chave) ?? 0,
    })
  }

  return result
}

export const comercialContatosPortalDashboardService = {
  getDefaultDashboardDateRange,
  fetchRelatorioContatosDashboard,
  fillPorDiaSeries,
}
